import { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Table
} from 'reactstrap'
import { Spinner } from 'react-bootstrap'
import { API_URL } from '../utils/Constants/URLS'


const ProformaInvoiceModal = ({ isOpen, toggle, salesOrder, onSuccess }) => {

  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  // ─── Top-level form state ──────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    deliveryNote: '',
    modeTermsOfPayment: '',
    referenceNo: '',
    otherReference: '',
    purchaseOrderNo: '',
    purchaseOrderDate: '',
    dispatchDocumentNo: '',
    dispatchDocumentDate: '',
    dispatchThrough: '',
    destination: ''
  })

  // ─── Editable line items (cloned from sales order) ─────────────────────────
  const [lineItems, setLineItems] = useState([])

  // ─── Editable payment terms (cloned from sales order) ──────────────────────
  const [paymentTerms, setPaymentTerms] = useState([])

  // ─── Payment basis: 'basic' | 'withGST' ───────────────────────────────────
  const [paymentBasis, setPaymentBasis] = useState('basic')

  // ─── Per-row selection (checkboxes) ────────────────────────────────────────
  const [checkedItems, setCheckedItems] = useState([])

  // ─── UI states ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Discount = ((quotationPrice - sellingPrice) / quotationPrice) * 100
   * sellingPrice = listPrice (rate from SO)
   */
  const calcDiscount = (sellingPrice, quotationPrice) => {
    const sp = Number(sellingPrice || 0)
    const qp = Number(quotationPrice || 0)
    if (qp <= 0 || sp <= 0 || qp <= sp) return 0
    return Number((((qp - sp) / qp) * 100).toFixed(2))
  }

  // ─── Seed form when modal opens / sales order changes ──────────────────────
  useEffect(() => {
    if (isOpen && salesOrder) {
      setFormData({
        deliveryNote: '',
        modeTermsOfPayment: '',
        referenceNo: '',
        otherReference: '',
        purchaseOrderNo: salesOrder?.customerPONumber || '',
        purchaseOrderDate: salesOrder?.customerPODate
          ? salesOrder.customerPODate.substring(0, 10)
          : '',
        dispatchDocumentNo: '',
        dispatchDocumentDate: '',
        dispatchThrough: '',
        destination: salesOrder?.shippingAddress || ''
      })

      // Deep-clone line items for safe editing
      const cloned = (salesOrder?.lineItems || []).map(item => {
        // sellingPrice = rate from the sales order
        const sellingPrice = Number(item?.rate || 0)
        // quotationPrice starts as same as sellingPrice (user can edit)
        const quotationPrice = sellingPrice
        const discount = calcDiscount(sellingPrice, quotationPrice)
        const qty = Number(item?.quantity || 0)
        const value = sellingPrice * qty

        return {
          _id: item._id,
          modelId: item?.model?._id || item?.model || '',
          modelName: item?.model?.modelName || '',
          partCode: item?.model?.partCode || '',
          uom: item?.model?.uom || '',
          hsnCode: item?.model?.HSNNumber || '',
          standardLeadTime: item?.standardLeadTime || null,
          committedDeliveryDate: item?.committedDeliveryDate || null,
          narration: '',
          gst: '',
          quantity: qty,
          // Selling Price = rate from SO (labeled "Selling Price")
          listPrice: sellingPrice,
          // Quotation Price - editable by user
          quotationPrice: quotationPrice,
          // Discount computed from quotationPrice vs sellingPrice
          discount: discount,
          // Rate = sellingPrice (for value calculation)
          rate: sellingPrice,
          value: Number(value.toFixed(2))
        }
      })
      setLineItems(cloned)
      // All items checked by default
      setCheckedItems(cloned.map((_, i) => i))

      // Deep-clone payment terms for safe editing
      setPaymentTerms(
        (salesOrder?.paymentTerms || []).map(pt => ({
          type: pt?.type || '',
          action: pt?.action || 'add',
          percentage: pt?.percentage || 0,
          amount: pt?.amount || 0,
          otherDetails: pt?.otherDetails || ''
        }))
      )

      setApiError(null)
    }
  }, [isOpen, salesOrder])

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (apiError) setApiError(null)
    // Clear the specific field's validation error on change
    if (validationErrors[field]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[field]; return n })
    }
  }

  /**
   * Line item change handler.
   * When quotationPrice changes → recompute discount.
   * When quantity changes → recompute value.
   */
  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }

      const item = updated[index]
      const sellingPrice = Number(item.listPrice || 0)
      const quotationPrice = Number(
        field === 'quotationPrice' ? value : item.quotationPrice || 0
      )
      const qty = Number(field === 'quantity' ? value : item.quantity || 0)

      // Recompute discount whenever quotationPrice changes
      updated[index].discount = calcDiscount(sellingPrice, quotationPrice)

      // Value = sellingPrice × qty
      updated[index].value = Number((sellingPrice * qty).toFixed(2))

      return updated
    })
  }

  const handlePaymentTermChange = (index, field, rawValue) => {
    setPaymentTerms(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: rawValue }

      // Derive base according to the chosen paymentBasis
      const selectedItems = lineItems.filter((_, i) => checkedItems.includes(i))
      const base = selectedItems.reduce((acc, item) => {
        const val = parseFloat(item.value) || 0
        if (paymentBasis === 'withGST') {
          const gstPct = parseFloat(item.gst) || 0
          return acc + val + (val * gstPct) / 100
        }
        return acc + val
      }, 0)

      if (field === 'percentage') {
        const pct = parseFloat(rawValue) || 0
        updated[index].amount = base > 0
          ? Number(((pct / 100) * base).toFixed(2))
          : 0
      } else if (field === 'amount') {
        const amt = parseFloat(rawValue) || 0
        updated[index].percentage = base > 0
          ? Number(((amt / base) * 100).toFixed(4))
          : 0
      }

      return updated
    })
  }

  const handleAddPaymentTerm = () => {
    setPaymentTerms(prev => [
      ...prev,
      { type: '', action: 'less', percentage: 0, amount: 0, otherDetails: '' }
    ])
  }

  const handleRemovePaymentTerm = index => {
    setPaymentTerms(prev => prev.filter((_, i) => i !== index))
  }

  const handleClose = () => {
    if (!loading) toggle()
  }

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    // ── Client-side validation ────────────────────────────────────────────────
    const errors = {}
    const mandatoryFields = [
      { key: 'deliveryNote', label: 'Delivery Note' },
      { key: 'modeTermsOfPayment', label: 'Mode / Terms of Payment' },
      { key: 'referenceNo', label: 'Reference No' },
      { key: 'otherReference', label: 'Other Reference' },
      { key: 'purchaseOrderNo', label: 'Purchase Order No' },
      { key: 'purchaseOrderDate', label: 'Purchase Order Date' },
      { key: 'destination', label: 'Destination' },
    ]
    mandatoryFields.forEach(({ key, label }) => {
      if (!formData[key]?.toString().trim()) {
        errors[key] = `${label} is required`
      }
    })
    if (checkedItems.length === 0) {
      errors._lineItems = 'Please select at least one line item'
    } else {
      const hasMissingGst = lineItems
        .filter((_, i) => checkedItems.includes(i))
        .some(item => item.gst === '' || item.gst === null || item.gst === undefined)
      if (hasMissingGst) {
        errors._lineItems = 'Please select a GST slab for all selected line items'
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }
    setValidationErrors({})
    // ─────────────────────────────────────────────────────────────────────────
    try {
      setLoading(true)
      setApiError(null)

      const payload = {
        salesOrder: salesOrder?._id || salesOrder?.id,
        deliveryNote: formData.deliveryNote,
        modeTermsOfPayment: formData.modeTermsOfPayment,
        referenceNo: formData.referenceNo,
        otherReference: formData.otherReference,
        purchaseOrderNo: formData.purchaseOrderNo,
        purchaseOrderDate: formData.purchaseOrderDate,
        dispatchDocumentNo: formData.dispatchDocumentNo,
        dispatchDocumentDate: formData.dispatchDocumentDate,
        dispatchThrough: formData.dispatchThrough,
        destination: formData.destination,
        paymentBasis,
        lineItems: lineItems.filter((_, i) => checkedItems.includes(i)).map(item => ({
          model: item.modelId,
          quantity: Number(item.quantity),
          // listPrice = Selling Price (rate from SO)
          listPrice: Number(item.listPrice),
          narration: item.narration || '',
          quotationPrice: Number(item.quotationPrice),
          discount: Number(item.discount),
          // rate = sellingPrice (used for value)
          rate: Number(item.listPrice),
          value: Number(item.value),
          standardLeadTime: item.standardLeadTime || null,
          committedDeliveryDate: item.committedDeliveryDate || null,
          gst: item.gst !== '' ? Number(item.gst) : 0
        })),
        paymentTerms: paymentTerms.map(pt => ({
          type: pt.type,
          action: pt.action || 'add',
          percentage: Number(pt.percentage),
          amount: Number(pt.amount),
          otherDetails: pt.otherDetails || ''
        }))
      }

      const response = await fetch(`${API_URL}proforma-invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errText = await response.text()
        let errMsg = 'Failed to create Proforma Invoice'
        try {
          const errJson = JSON.parse(errText)
          errMsg = errJson.message || errJson.error || errMsg
        } catch {
          errMsg = errText || errMsg
        }
        throw new Error(errMsg)
      }

      const result = await response.json()
      if (onSuccess) onSuccess(result)
      toggle()
    } catch (err) {
      console.error('Proforma Invoice error:', err)
      setApiError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  // ─── Totals ────────────────────────────────────────────────────────────────
  const selectedItems = lineItems.filter((_, i) => checkedItems.includes(i))

  const totalValue = lineItems.reduce(
    (acc, item) => acc + (parseFloat(item.value) || 0),
    0
  )

  // Payment base respects the user's paymentBasis choice and selected items
  const paymentBase = selectedItems.reduce((acc, item) => {
    const val = parseFloat(item.value) || 0
    if (paymentBasis === 'withGST') {
      const gstPct = parseFloat(item.gst) || 0
      return acc + val + (val * gstPct) / 100
    }
    return acc + val
  }, 0)

  // Keep for backward-compat (running balance display)
  const totalValueWithGST = paymentBase

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size='xl'
      centered
      scrollable
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
    >
      <ModalHeader toggle={handleClose}>
        <i className='bi bi-file-earmark-text me-2'></i>
        Create Proforma Invoice
        {salesOrder?.salesOrderNumber && (
          <small className='text-muted ms-2'>
            — SO# {salesOrder.salesOrderNumber}
          </small>
        )}
      </ModalHeader>

      <ModalBody>
        {/* Error Banner */}
        {apiError && (
          <div className='alert alert-danger mb-3'>
            <i className='bi bi-exclamation-triangle me-2'></i>
            {apiError}
          </div>
        )}

        {/* ── Section 1: Header Details ────────────────────────────────── */}
        <h6 className='fw-bold text-primary border-bottom pb-2 mb-3'>
          Header Details
        </h6>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for='pi-deliveryNote'>Delivery Note <span className='text-danger'>*</span></Label>
              <Input
                type='text'
                id='pi-deliveryNote'
                placeholder='Enter delivery note'
                value={formData.deliveryNote}
                onChange={e => handleFieldChange('deliveryNote', e.target.value)}
                disabled={loading}
                invalid={!!validationErrors.deliveryNote}
              />
              {validationErrors.deliveryNote && (
                <small className='text-danger'>{validationErrors.deliveryNote}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-modeTermsOfPayment'>Mode / Terms of Payment <span className='text-danger'>*</span></Label>
              <Input
                type='text'
                id='pi-modeTermsOfPayment'
                placeholder='e.g., 30 days from invoice date'
                value={formData.modeTermsOfPayment}
                onChange={e =>
                  handleFieldChange('modeTermsOfPayment', e.target.value)
                }
                disabled={loading}
                invalid={!!validationErrors.modeTermsOfPayment}
              />
              {validationErrors.modeTermsOfPayment && (
                <small className='text-danger'>{validationErrors.modeTermsOfPayment}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-referenceNo'>Reference No <span className='text-danger'>*</span></Label>
              <Input
                type='text'
                id='pi-referenceNo'
                placeholder='Enter reference number'
                value={formData.referenceNo}
                onChange={e => handleFieldChange('referenceNo', e.target.value)}
                disabled={loading}
                invalid={!!validationErrors.referenceNo}
              />
              {validationErrors.referenceNo && (
                <small className='text-danger'>{validationErrors.referenceNo}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-otherReference'>Other Reference <span className='text-danger'>*</span></Label>
              <Input
                type='text'
                id='pi-otherReference'
                placeholder='Enter other reference'
                value={formData.otherReference}
                onChange={e =>
                  handleFieldChange('otherReference', e.target.value)
                }
                disabled={loading}
                invalid={!!validationErrors.otherReference}
              />
              {validationErrors.otherReference && (
                <small className='text-danger'>{validationErrors.otherReference}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-purchaseOrderNo'>
                Purchase Order No <span className='text-danger'>*</span>{' '}
                <small className='text-muted'>(from SO)</small>
              </Label>
              <Input
                type='text'
                id='pi-purchaseOrderNo'
                value={formData.purchaseOrderNo}
                onChange={e =>
                  handleFieldChange('purchaseOrderNo', e.target.value)
                }
                disabled={loading}
                invalid={!!validationErrors.purchaseOrderNo}
              />
              {validationErrors.purchaseOrderNo && (
                <small className='text-danger'>{validationErrors.purchaseOrderNo}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-purchaseOrderDate'>
                Purchase Order Date <span className='text-danger'>*</span>{' '}
                <small className='text-muted'>(from SO)</small>
              </Label>
              <Input
                type='date'
                id='pi-purchaseOrderDate'
                value={formData.purchaseOrderDate}
                onChange={e =>
                  handleFieldChange('purchaseOrderDate', e.target.value)
                }
                disabled={loading}
                invalid={!!validationErrors.purchaseOrderDate}
              />
              {validationErrors.purchaseOrderDate && (
                <small className='text-danger'>{validationErrors.purchaseOrderDate}</small>
              )}
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-dispatchDocumentNo'>Dispatch Document No</Label>
              <Input
                type='text'
                id='pi-dispatchDocumentNo'
                placeholder='Enter dispatch document number'
                value={formData.dispatchDocumentNo}
                onChange={e =>
                  handleFieldChange('dispatchDocumentNo', e.target.value)
                }
                disabled={loading}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-dispatchDocumentDate'>Dispatch Document Date</Label>
              <Input
                type='date'
                id='pi-dispatchDocumentDate'
                value={formData.dispatchDocumentDate}
                onChange={e =>
                  handleFieldChange('dispatchDocumentDate', e.target.value)
                }
                disabled={loading}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-dispatchThrough'>Dispatch Through</Label>
              <Input
                type='text'
                id='pi-dispatchThrough'
                placeholder='e.g., DTDC, FedEx'
                value={formData.dispatchThrough}
                onChange={e =>
                  handleFieldChange('dispatchThrough', e.target.value)
                }
                disabled={loading}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label for='pi-destination'>Destination <span className='text-danger'>*</span></Label>
              <Input
                type='text'
                id='pi-destination'
                placeholder='Enter destination'
                value={formData.destination}
                onChange={e => handleFieldChange('destination', e.target.value)}
                disabled={loading}
                invalid={!!validationErrors.destination}
              />
              {validationErrors.destination && (
                <small className='text-danger'>{validationErrors.destination}</small>
              )}
            </FormGroup>
          </Col>
        </Row>

        {/* ── Section 2: Line Items ─────────────────────────────────────── */}
        <h6 className='fw-bold text-primary border-bottom pb-2 mb-3 mt-4'>
          Line Items
        </h6>
        {validationErrors._lineItems && (
          <div className='alert alert-warning py-2 mb-2' style={{ fontSize: '0.87rem' }}>
            <i className='bi bi-exclamation-triangle me-2'></i>
            {validationErrors._lineItems}
          </div>
        )}
        <div className='table-responsive'>
          <Table bordered size='sm' className='align-middle'>
            <thead className='table-light'>
              <tr>
                <th style={{ minWidth: 36 }}>
                  <input
                    type='checkbox'
                    title='Select/Deselect all'
                    checked={checkedItems.length === lineItems.length && lineItems.length > 0}
                    onChange={e => {
                      if (e.target.checked) {
                        setCheckedItems(lineItems.map((_, i) => i))
                        // Clear line-item validation error when items are selected
                        if (validationErrors._lineItems) {
                          setValidationErrors(prev => { const n = { ...prev }; delete n._lineItems; return n })
                        }
                      } else {
                        setCheckedItems([])
                      }
                    }}
                    disabled={loading}
                  />
                </th>
                <th style={{ minWidth: 40 }}>#</th>
                <th style={{ minWidth: 160 }}>Model</th>
                <th style={{ minWidth: 150 }}>Narration</th>
                <th style={{ minWidth: 100 }}>Part Code</th>
                <th style={{ minWidth: 70 }}>HSN</th>
                <th style={{ minWidth: 80 }}>GST %</th>
                <th style={{ minWidth: 55 }}>UOM</th>
                <th style={{ minWidth: 80 }}>Qty</th>
                <th style={{ minWidth: 110 }}>Selling Price</th>
                <th style={{ minWidth: 120 }}>Quotation Price</th>
                <th style={{ minWidth: 100 }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={13} className='text-center text-muted py-3'>
                    No line items found in the sales order.
                  </td>
                </tr>
              ) : (
                lineItems.map((item, idx) => (
                  <tr
                    key={item._id || idx}
                    style={{ opacity: checkedItems.includes(idx) ? 1 : 0.45 }}
                  >
                    {/* Row checkbox */}
                    <td className='text-center'>
                      <input
                        type='checkbox'
                        checked={checkedItems.includes(idx)}
                        onChange={e => {
                          if (e.target.checked) {
                            setCheckedItems(prev => [...prev, idx])
                            // Clear line-item validation error as soon as one item is selected
                            if (validationErrors._lineItems) {
                              setValidationErrors(prev => { const n = { ...prev }; delete n._lineItems; return n })
                            }
                          } else {
                            setCheckedItems(prev => prev.filter(i => i !== idx))
                          }
                        }}
                        disabled={loading}
                      />
                    </td>
                    <td>{idx + 1}</td>
                    <td>{item?.modelName}</td>
                    <td>
                      <Input
                        type='text'
                        bsSize='sm'
                        placeholder='Narration'
                        value={item.narration}
                        onChange={e =>
                          handleLineItemChange(idx, 'narration', e.target.value)
                        }
                        disabled={loading || !checkedItems.includes(idx)}
                      />
                    </td>
                    <td>{item?.partCode}</td>
                    <td>{item?.hsnCode}</td>
                    <td>
                      <Input
                        type='select'
                        bsSize='sm'
                        value={item.gst}
                        onChange={e =>
                          handleLineItemChange(idx, 'gst', e.target.value)
                        }
                        disabled={loading || !checkedItems.includes(idx)}
                      >
                        <option value=''>Select</option>
                        <option value={0.1}>0.1</option>
                        <option value={5}>5</option>
                        <option value={12}>12</option>
                        <option value={18}>18</option>
                        <option value={28}>28</option>
                      </Input>
                    </td>
                    <td>{item?.uom}</td>
                    <td>{item?.quantity}</td>
                    <td className='text-end'>
                      {(parseFloat(item?.listPrice) || 0).toFixed(2)}
                    </td>
                    <td>
                      <Input
                        type='number'
                        bsSize='sm'
                        min={0}
                        step='0.01'
                        onWheel={e => e.target.blur()}
                        value={item.quotationPrice}
                        onChange={e =>
                          handleLineItemChange(idx, 'quotationPrice', e.target.value)
                        }
                        disabled={loading || !checkedItems.includes(idx)}
                        style={{ width: 110 }}
                      />
                    </td>
                    <td className='text-end'>
                      {(parseFloat(item?.value) || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {lineItems.length > 0 && (
              <tfoot className='table-light'>
                <tr>
                  <td colSpan={10} className='text-end fw-bold'>
                    Total Value (selected)
                  </td>
                  <td className='text-end fw-bold'>
                    {selectedItems.reduce((a, it) => a + (parseFloat(it.value) || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </Table>
        </div>

        {/* ── Section 3: Payment Terms ──────────────────────────────────── */}
        <h6 className='fw-bold text-primary border-bottom pb-2 mb-3 mt-4'>
          Payment Terms
        </h6>

        {/* Payment basis toggle */}
        <div className='d-flex align-items-center gap-3 mb-3'>
          <span className='fw-semibold' style={{ fontSize: '0.9rem' }}>Calculate % on:</span>
          <div className='form-check form-check-inline mb-0'>
            <input
              className='form-check-input'
              type='radio'
              id='pb-basic'
              name='paymentBasis'
              value='basic'
              checked={paymentBasis === 'basic'}
              onChange={() => setPaymentBasis('basic')}
              disabled={loading}
            />
            <label className='form-check-label' htmlFor='pb-basic'>Basic (excl. GST)</label>
          </div>
          <div className='form-check form-check-inline mb-0'>
            <input
              className='form-check-input'
              type='radio'
              id='pb-withGST'
              name='paymentBasis'
              value='withGST'
              checked={paymentBasis === 'withGST'}
              onChange={() => setPaymentBasis('withGST')}
              disabled={loading}
            />
            <label className='form-check-label' htmlFor='pb-withGST'>Basic + GST</label>
          </div>
          <span className='text-muted ms-2' style={{ fontSize: '0.82rem' }}>
            Base: ₹{paymentBase.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className='table-responsive'>
          <Table bordered size='sm' className='align-middle'>
            <thead className='table-light'>
              <tr>
                <th>#</th>
                <th>Type / Milestone</th>
                <th style={{ minWidth: 100 }}>Action</th>
                <th style={{ minWidth: 110 }}>Percentage (%)</th>
                <th style={{ minWidth: 120 }}>Amount (₹)</th>
                <th style={{ minWidth: 120 }}>Balance (₹)</th>
                <th style={{ width: 42 }}></th>
              </tr>
            </thead>
            <tbody>
              {paymentTerms.length === 0 ? (
                <tr>
                  <td colSpan={7} className='text-center text-muted py-3'>
                    No payment terms. Click &quot;+ Add Row&quot; to add one.
                  </td>
                </tr>
              ) : (
                paymentTerms.map((pt, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>

                    {/* Type */}
                    <td>
                      <Input
                        type='text'
                        bsSize='sm'
                        value={pt.type}
                        onChange={e =>
                          handlePaymentTermChange(idx, 'type', e.target.value)
                        }
                        disabled={loading}
                      />
                    </td>

                    {/* Action – Add / Less */}
                    <td>
                      <Input
                        type='select'
                        bsSize='sm'
                        value={pt.action}
                        onChange={e =>
                          handlePaymentTermChange(idx, 'action', e.target.value)
                        }
                        disabled={loading}
                      >
                        <option value='add'>Add</option>
                        <option value='less'>Less</option>
                        <option value='display'>Display</option>
                      </Input>
                    </td>

                    {/* Percentage */}
                    <td>
                      <Input
                        type='number'
                        bsSize='sm'
                        step='0.01'
                        min={0}
                        max={100}
                        onWheel={e => e.target.blur()}
                        value={pt.percentage}
                        onChange={e =>
                          handlePaymentTermChange(
                            idx,
                            'percentage',
                            e.target.value
                          )
                        }
                        disabled={loading}
                      />
                    </td>

                    {/* Amount */}
                    <td>
                      <Input
                        type='number'
                        bsSize='sm'
                        step='0.01'
                        min={0}
                        onWheel={e => e.target.blur()}
                        value={pt.amount}
                        onChange={e =>
                          handlePaymentTermChange(idx, 'amount', e.target.value)
                        }
                        disabled={loading}
                      />
                    </td>

                    {/* Running Balance – display-only (GST-inclusive base) */}
                    <td
                      className='text-end fw-semibold'
                      style={{
                        color: (() => {
                          let bal = totalValueWithGST
                          paymentTerms.slice(0, idx + 1).forEach(p => {
                            const a = Number(p.amount || 0)
                            if (p.action === 'less') bal -= a
                            else if (p.action === 'add') bal += a
                          })
                          return bal < 0 ? '#dc3545' : '#198754'
                        })()
                      }}
                    >
                      ₹{(() => {
                        let bal = totalValueWithGST
                        paymentTerms.slice(0, idx + 1).forEach(p => {
                          const a = Number(p.amount || 0)
                          if (p.action === 'less') bal -= a
                          else if (p.action === 'add') bal += a
                        })
                        return bal.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })
                      })()}
                    </td>

                    {/* Delete row */}
                    <td className='text-center'>
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-danger py-0 px-1'
                        title='Remove row'
                        onClick={() => handleRemovePaymentTerm(idx)}
                        disabled={loading}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {paymentTerms.length > 0 && (
              <tfoot className='table-light'>
                <tr>
                  <td colSpan={3} className='text-end fw-bold text-muted' style={{ fontSize: '0.85rem' }}>
                    Base ({paymentBasis === 'withGST' ? 'Basic + GST' : 'Basic only'})
                  </td>
                  <td colSpan={2} className='text-end fw-semibold' style={{ fontSize: '0.85rem' }}>
                    ₹{paymentBase.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={3} className='text-end fw-bold'>
                    Net Payable
                  </td>
                  <td colSpan={2} className='text-end fw-bold'>
                    ₹{(() => {
                      let bal = totalValueWithGST
                      paymentTerms.forEach(pt => {
                        const amt = Number(pt.amount || 0)
                        if (pt.action === 'less') bal -= amt
                        else if (pt.action === 'add') bal += amt
                      })
                      return bal.toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })
                    })()}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
        <div className='d-flex justify-content-end mb-3'>
          <Button
            color='success'
            size='sm'
            outline
            onClick={handleAddPaymentTerm}
            disabled={loading}
          >
            + Add Row
          </Button>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color='secondary' onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            <>
              <i className='bi bi-file-earmark-check me-2'></i>
              Create Proforma Invoice
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ProformaInvoiceModal
